import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PushNotifications } from '@capacitor/push-notifications';
import { Notification, Paginated } from '@haleo-frontend/data-access/models';
import { AuthService, ClientService, ConsentFormService } from '@haleo-frontend/data-access/services';
import { EnvironmentUtils } from '@haleo-frontend/utils';
import { Platform } from '@ionic/angular';
import { lastValueFrom, Observable, ReplaySubject } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { flatMap, map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class NotificationService {

    private initiated: boolean;
    firebasePlugin: any;
    token: ReplaySubject<string>;

    constructor(private platform: Platform,
                private clientService: ClientService,
                private authService: AuthService,
                private router: Router,
                private http: HttpClient,
                private consentFormService: ConsentFormService) {
        this.token = new ReplaySubject<string>(1);
    }

    init(bypassAuth = false): Observable<void> {

        return fromPromise(this.platform.ready())
            .pipe(flatMap(() => this.authService.isAuthenticated()),
                map(isAuthenticated => {

                    if ((isAuthenticated || bypassAuth) && !this.initiated && this.platform.is('capacitor')) {
                        this.initPlugin(bypassAuth);
                    }
                }));
    }

    clear() {

        if (this.platform.is('capacitor')) {
            try {
                PushNotifications.removeAllDeliveredNotifications();
            } catch (e) {
                console.error(e);
            }

        }
    }

    getInApp(): Observable<Notification[]> {
        return this.http.get<Paginated>(EnvironmentUtils.env.api + `notifications`)
            .pipe(map(data => data.data));
    }

    dismissedInApp(notification: Notification): Observable<Notification> {
        const data = {
            op: 'replace',
            path: '/status',
            value: 'dismissed'
        };
        return this.http.put<Notification>(EnvironmentUtils.env.api + `notifications/${notification.id}`, data);
    }

    doneInApp(notification: Notification): Observable<Notification> {
        const data = {
            op: 'replace',
            path: '/status',
            value: 'done'
        };
        return this.http.put<Notification>(EnvironmentUtils.env.api + `notifications/${notification.id}`, data);
    }

    async clearConsentFormReminder() {

        const consentForms = await lastValueFrom(this.consentFormService.getConsentFormsToAccept());

        if (consentForms.length === 0) {

            const notifications = await lastValueFrom(this.getInApp());
            const reminder = notifications.find(notification => notification.actionKey === 'openConsentForm');

            if (reminder) {
                this.doneInApp(reminder).subscribe();
            }
        }
    }

    private async initPlugin(bypassAuth: boolean) {

        await PushNotifications.addListener('registration', token => this.saveToken(token.value, bypassAuth));
        await PushNotifications.addListener('pushNotificationActionPerformed',
            notification => this.onNotification({wasTapped: true, ...notification.notification.data}));

        let permStatus = await PushNotifications.checkPermissions();

        if (permStatus.receive === 'prompt') {
            permStatus = await PushNotifications.requestPermissions();
        }

        if (permStatus.receive === 'granted') {
            await PushNotifications.register();
        }

        this.initiated = true;
        this.clear();
    }

    private saveToken(token: string, bypassAuth: boolean) {

        if (!bypassAuth) {
            this.clientService.update({pushToken: token}).subscribe();
        }

        this.token.next(token);
        this.token.complete();
    }

    private async onNotification(notification: any) {

        if (notification && notification.wasTapped && notification.name) {

            switch (notification.name) {
                case 'fillSleepDiaryReminder':
                    this.router.navigate(['/tabs/sleep-diary']);
                    break;
                case 'therapySession1hReminder':
                case 'therapySession10minutesReminder':
                    this.router.navigate(['/tabs/home']);
                    break;
                case 'dailyCheckIn':
                case 'fillSleepDiaryReminderSelfGuided':
                case 'fillSleepDiaryReminderSelfGuidedNoon':
                    this.router.navigate(['/sleep-diary-reminder']);
                    break;
            }
        }
    }
}
